import { useEffect, useState } from 'react';
import { useT, type TFn } from '@/i18n/LocaleProvider';

/**
 * What the sign-in screen calls its passkey button (I-0653 AC-2).
 *
 * The button used to say "Sign in with Touch ID" everywhere — on an iPhone with Face ID, on a
 * Windows laptop, on an Android phone. A brand name is only worth showing when it is the thing that
 * will actually appear, so the label is built from two facts:
 *
 * 1. **Does this device have a built-in authenticator at all?** WebAuthn answers that with
 *    `isUserVerifyingPlatformAuthenticatorAvailable()`. Without one the ceremony will offer a
 *    security key or a nearby phone, so no device brand is true.
 * 2. **Which platform is it?** Only two platforms have exactly one name for their built-in
 *    authenticator: a Mac's is Touch ID, and Windows' is Windows Hello. An iPhone or iPad may be
 *    Face ID or Touch ID, and an Android phone may be a fingerprint, a face or a screen lock — the
 *    web cannot tell which, so those get the generic name.
 *
 * ⛔ Anything unsure falls back to "Sign in with a passkey". A generic name is never wrong; a wrong
 * brand tells somebody to reach for a sensor their device does not have.
 *
 * ⚠️ This names the button and gates nothing. `AuthStore.assertWebAuthnUsable` deliberately does not
 * consult the platform-authenticator check, because a security key or a phone completes the same
 * ceremony — and the button stays enabled whatever this answers.
 */

export type PasskeyPlatform = 'mac' | 'windows' | 'ios' | 'android' | 'other';

export const GENERIC_PASSKEY_LABEL = 'Sign in with a passkey';

export interface IPasskeyPlatformHints {
  userAgent: string;
  /** `navigator.userAgentData.platform` — Chromium only; absent on Safari and Firefox. */
  uaDataPlatform?: string | null;
  /** `navigator.maxTouchPoints` — the only way to tell an iPad from a Mac, see below. */
  maxTouchPoints?: number;
}

/**
 * The platform, from the hints a browser gives.
 *
 * ⚠️ **An iPad says it is a Mac.** iPadOS Safari requests desktop sites by default and sends a
 * `Macintosh` user agent, so the user-agent string alone would label every iPad "Touch ID" — wrong
 * on every iPad with Face ID. No Mac has a touch screen, so a `Macintosh` agent with touch points is
 * an iPad.
 */
export function passkeyPlatform({
  userAgent,
  uaDataPlatform,
  maxTouchPoints = 0,
}: IPasskeyPlatformHints): PasskeyPlatform {
  const hint = (uaDataPlatform ?? '').toLowerCase();
  const agent = userAgent.toLowerCase();

  if (hint === 'android' || agent.includes('android')) return 'android';
  if (hint === 'ios' || /iphone|ipad|ipod/.test(agent)) return 'ios';
  if (hint === 'macos' || agent.includes('macintosh') || agent.includes('mac os x')) {
    return maxTouchPoints > 1 ? 'ios' : 'mac';
  }
  if (hint === 'windows' || agent.includes('windows')) return 'windows';
  return 'other';
}

/**
 * The built-in authenticator's name, where exactly one name is true, and `null` everywhere else.
 * `hasPlatformAuthenticator` is `null` while the browser has not answered yet, and `false` where it
 * cannot answer — both read as "no brand is known to be true".
 *
 * ⭐ The one source for every surface that names the sensor: the sign-in button here, and the
 * profile page's section and a new passkey's default name (`I-0658`). Two copies of this rule is
 * how the profile page went on saying "Touch ID" on Windows after the button stopped.
 */
export function passkeyAuthenticatorName(
  platform: PasskeyPlatform,
  hasPlatformAuthenticator: boolean | null,
): string | null {
  if (hasPlatformAuthenticator !== true) return null;
  if (platform === 'mac') return 'Touch ID';
  if (platform === 'windows') return 'Windows Hello';
  return null;
}

/** The button's words. */
export function passkeySignInLabel(
  platform: PasskeyPlatform,
  hasPlatformAuthenticator: boolean | null,
): string {
  const name = passkeyAuthenticatorName(platform, hasPlatformAuthenticator);
  return name ? `Sign in with ${name}` : GENERIC_PASSKEY_LABEL;
}

/** A new passkey's name when nothing surer is known — the device's sensor, or the host name. */
export const GENERIC_PASSKEY_NAME = 'Passkey';

/**
 * What the profile page calls its passkey section (`I-0658`). The sensor is named only where
 * `passkeyAuthenticatorName` is sure of it; elsewhere the words describe a passkey on this device.
 */
export function passkeySectionCopy(
  authenticatorName: string | null,
  t: TFn,
): {
  heading: string;
  description: string;
} {
  // F-0621: in the reader's language (`profile.passkeys`); the sensor's name stays its name.
  if (!authenticatorName) {
    return {
      heading: t('profile.passkeys.heading'),
      description: t('profile.passkeys.description'),
    };
  }
  return {
    heading: t('profile.passkeys.headingNamed', { name: authenticatorName }),
    description: t('profile.passkeys.descriptionNamed', { name: authenticatorName }),
  };
}

/** Whether the label names a device sensor, so the screen can pick a matching icon. */
export function labelNamesBiometricSensor(label: string): boolean {
  return label !== GENERIC_PASSKEY_LABEL;
}

export function currentHints(): IPasskeyPlatformHints {
  if (typeof navigator === 'undefined') return { userAgent: '' };
  return {
    userAgent: navigator.userAgent ?? '',
    uaDataPlatform: navigator.userAgentData?.platform ?? null,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
  };
}

/**
 * Whether this browser has a built-in authenticator: `null` until WebAuthn has answered — the answer
 * is local and arrives within a frame — and `false` where it cannot answer.
 */
function usePlatformAuthenticator(): boolean | null {
  const [hasPlatformAuthenticator, setHasPlatformAuthenticator] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    const credential = typeof window !== 'undefined' ? window.PublicKeyCredential : undefined;
    const ask = credential?.isUserVerifyingPlatformAuthenticatorAvailable;
    if (typeof ask !== 'function') {
      setHasPlatformAuthenticator(false);
      return undefined;
    }
    ask
      .call(credential)
      .then((available) => {
        if (!cancelled) setHasPlatformAuthenticator(available === true);
      })
      .catch(() => {
        if (!cancelled) setHasPlatformAuthenticator(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return hasPlatformAuthenticator;
}

/**
 * The label for this browser. Starts generic and names the sensor once WebAuthn has answered, and
 * until then the generic words are true.
 */
export function usePasskeySignInLabel(): { label: string; namesSensor: boolean } {
  const t = useT();
  const name = passkeyAuthenticatorName(
    passkeyPlatform(currentHints()),
    usePlatformAuthenticator(),
  );
  // I-1971: said in the reader's language; the sensor's own name (Touch ID, Windows Hello) stays.
  return {
    label: name ? t('auth.passkeySignIn.named', { name }) : t('auth.passkeySignIn.generic'),
    namesSensor: name !== null,
  };
}

/** The sensor's name for this browser, or `null` — the same answer the sign-in button reads. */
export function usePasskeyAuthenticatorName(): string | null {
  return passkeyAuthenticatorName(passkeyPlatform(currentHints()), usePlatformAuthenticator());
}
